import { AnimatePresence, motion } from 'framer-motion'
import { useCallback } from 'react'
import { MessageSquare, Smile, Plus } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { cn } from '@/lib/utils'
import { createComment } from '@/store'
import { ReactionPicker } from './ui/reaction-picker'
import { TaskComments } from './ui/task-comments'
import { useReactions } from './hooks/use-reactions'
const EMPTY_COMMENTS = []
const EMPTY_REACTIONS = {}
const getSafeComments = (comments) => (Array.isArray(comments) ? comments : EMPTY_COMMENTS)
const getSafeReactions = (reactions) => {
  if (!reactions || typeof reactions !== 'object' || Array.isArray(reactions)) {
    return EMPTY_REACTIONS
  }
  return reactions
}
const getReactionEntries = (reactions) =>
  Object.entries(reactions).filter(([, users]) => Array.isArray(users) && users.length > 0)
const ReactionButtons = ({ reactions, handleReactionClick, stopPropagation }) => (
  <AnimatePresence mode="popLayout">
    {getReactionEntries(reactions).map(([emoji, users]) => {
      const isReacted = users.includes('me')
      return (
        <motion.div
          key={emoji}
          layout="position"
          initial={{
            opacity: 0,
          }}
          animate={{
            opacity: 1,
          }}
          exit={{
            opacity: 0,
          }}
        >
          <Button
            variant="secondary"
            size="sm"
            data-emoji={emoji}
            onClick={handleReactionClick}
            onPointerDown={stopPropagation}
            className={cn(
              'rounded-full h-6 px-1.5 gap-1.5 font-bold text-[10px] border shadow-none transition-all active:opacity-70',
              isReacted
                ? 'bg-primary/20 text-primary border-primary/30'
                : 'bg-muted border-transparent text-muted-foreground hover:bg-muted/80',
            )}
          >
            <span className="text-xs leading-none select-none">{emoji}</span>
            <span>{users.length}</span>
          </Button>
        </motion.div>
      )
    })}
  </AnimatePresence>
)
const ReactionPickerPopover = ({ reactions, onSelect, stopPropagation }) => (
  <Popover>
    <PopoverTrigger asChild>
      <Button
        variant="ghost"
        size="sm"
        className="h-6 px-1.5 gap-0.5 rounded-full text-muted-foreground border border-border/20 hover:text-foreground hover:bg-muted transition-all"
        onClick={stopPropagation}
        onPointerDown={stopPropagation}
      >
        <Smile className="h-3 w-3" />
        <Plus className="h-2.5 w-2.5" />
      </Button>
    </PopoverTrigger>
    <PopoverContent
      className="w-[min(340px,calc(100vw-1rem))] p-0 rounded-xl border-none shadow-2xl shadow-primary/5 p-1 bg-popover/95 backdrop-blur-xl"
      align="start"
      onPointerDown={stopPropagation}
      onClick={stopPropagation}
    >
      <ReactionPicker
        selectedEmojis={Object.entries(reactions)
          .filter(([, users]) => Array.isArray(users) && users.includes('me'))
          .map(([emoji]) => emoji)}
        onSelect={onSelect}
      />
    </PopoverContent>
  </Popover>
)
const CommentsToggleButton = ({
  isCommentsExpanded,
  commentsCount,
  isBoard,
  onClick,
  stopPropagation,
}) => (
  <Button
    variant="ghost"
    size="sm"
    className={cn(
      'h-6 gap-1 font-bold text-[10px] transition-all rounded-lg px-2',
      isCommentsExpanded
        ? 'text-primary bg-primary/10'
        : 'text-muted-foreground hover:text-foreground hover:bg-muted',
    )}
    onClick={onClick}
    onPointerDown={stopPropagation}
  >
    <MessageSquare className={cn('h-3 w-3', isCommentsExpanded && 'fill-current')} />
    {commentsCount} {isBoard ? '' : 'Comments'}
  </Button>
)
export function TodoItemFooter({
  todo,
  enabledFeatures,
  onUpdate,
  isCommentsExpanded,
  onToggleComments,
  viewMode = 'list',
}) {
  const isBoard = viewMode === 'board'
  const comments = getSafeComments(todo.comments)
  const reactions = getSafeReactions(todo.reactions)
  const stopPropagation = useCallback((e) => {
    e.stopPropagation()
  }, [])
  const handleAddComment = useCallback(
    (text) => {
      const comment = createComment(text)
      onUpdate(todo.id, {
        comments: [...comments, comment],
      })
    },
    [comments, onUpdate, todo.id],
  )
  const handleDeleteComment = useCallback(
    (commentId) => {
      onUpdate(todo.id, {
        comments: comments.filter((c) => c.id !== commentId),
      })
    },
    [comments, onUpdate, todo.id],
  )
  const { handleReactionClick, handleReactionSelect } = useReactions(todo, onUpdate)
  const handleToggleCommentsClick = useCallback(
    (e) => {
      e.stopPropagation()
      onToggleComments()
    },
    [onToggleComments],
  )
  return (
    <div className="flex flex-col gap-2">
      <AnimatePresence>
        {isCommentsExpanded && enabledFeatures.comments && (
          <motion.div
            initial={{
              opacity: 0,
              y: 10,
            }}
            animate={{
              opacity: 1,
              y: 0,
            }}
            exit={{
              opacity: 0,
              y: 10,
            }}
            transition={{
              duration: 0.2,
            }}
            className="overflow-hidden origin-bottom"
          >
            <TaskComments
              todoId={todo.id}
              comments={comments}
              onAddComment={handleAddComment}
              onDeleteComment={handleDeleteComment}
            />
          </motion.div>
        )}
      </AnimatePresence>

      <div
        className={cn(
          'flex flex-wrap items-center justify-between pt-2 border-t border-border/40 gap-2',
          isBoard && 'gap-1',
        )}
      >
        <div className="flex flex-wrap items-center gap-1 min-h-[28px]">
          <ReactionButtons
            reactions={reactions}
            handleReactionClick={handleReactionClick}
            stopPropagation={stopPropagation}
          />

          {enabledFeatures.reactions && (
            <ReactionPickerPopover
              reactions={reactions}
              onSelect={handleReactionSelect}
              stopPropagation={stopPropagation}
            />
          )}
        </div>

        {!isBoard && (
          <span className="text-xs font-medium text-muted-foreground/60 truncate max-w-[120px]">
            Last change by Alex M. 2d ago
          </span>
        )}

        {enabledFeatures.comments && (
          <CommentsToggleButton
            isCommentsExpanded={isCommentsExpanded}
            commentsCount={comments.length}
            isBoard={isBoard}
            onClick={handleToggleCommentsClick}
            stopPropagation={stopPropagation}
          />
        )}
      </div>
    </div>
  )
}
